import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Collapsible } from '@repo/design-system/components/ui/collapsible';
import { ContextMenuItem } from '@repo/design-system/components/ui/context-menu';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { overlayBorderStyles } from '@repo/design-system/custom-styles';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css, cx } from '@repo/panda/css';
import { useBulkUpdatePinStatusesMutation, useDeleteSavedChecklistMutation, useListSavedChecklistsQuery } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectAuthToken } from '@repo/store/maps/slices/auth';
import { selectActiveChecklistSubcategory, setActiveChecklistSubcategory, setActiveCustomChecklistId, setSidebarView } from '@repo/store/maps/slices/clientContext';
import { type FilterState, selectExternalMapId, selectFilterState, selectMapData, selectSteamId, selectTitleName, setFilterStateFromUrl } from '@repo/store/maps/slices/mapData';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import { getFontInfoForGlyph, getIconForGlyph, getSubcategoryIconStyle } from '@repo/types/maps/map-utils';
import { getPinImageUrl, getSteamImageUrl, getWandSteamCdnImageUrl } from '@repo/utility/image-urls';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { ExploreActionHeaderIcon } from '../explore/explore-action-header-icon';
import { useMapPinOperations } from '../game-map/hooks/useMapPinOperations';
import { MapDiscoveryProgress } from '../map-discovery-progress';
import { SidebarHeroFrame } from '../utils/sidebar-hero-frame';
import { ChecklistCard } from './checklist-card';
import { ChecklistFeedback } from './checklist-feedback';
import { ChecklistItems } from './checklist-items';
import { SearchFilters } from './checklist-search-filters';
import { ChecklistSubcategoryCard } from './checklist-subcategory-card';
import { buildSubcategoryMap } from './checklist-utils';
import { DeleteChecklistConfirmModal } from './delete-checklist-confirm-modal';
import { useChecklistFeedback } from './use-checklist-feedback';
import { useChecklistNavigation } from './use-checklist-navigation';
import { useChecklistState } from './use-checklist-state';
function getSavedChecklistCardDisplay(checklist: {
  pinIds: string[];
}, checkedItems: Set<string>, pinFeatureMap: Map<string, MapDataLocationFeature>, subcategoryMap: ReturnType<typeof buildSubcategoryMap> | null): {
  foundCount: number;
  imageUrl: string | null;
  iconChar: string;
  iconStyle: React.CSSProperties;
} {
  let foundCount = 0;
  let imageUrl: string | null = null;
  let iconFeature: MapDataLocationFeature | null = null;
  for (const pinId of checklist.pinIds) {
    if (checkedItems.has(pinId)) foundCount += 1;
    const feature = pinFeatureMap.get(pinId);
    if (!feature) continue;
    if (!iconFeature) iconFeature = feature;
    if (!imageUrl) {
      const url = getPinImageUrl(feature.properties.imageId, feature.properties.imagePath);
      if (url) {
        imageUrl = url;
        iconFeature = feature;
      }
    }
  }
  const glyph = iconFeature ? subcategoryMap?.get(iconFeature.properties.subcategoryExternalId)?.icon ?? 'category' : 'category';
  const fontInfo = getFontInfoForGlyph(glyph);
  return {
    foundCount,
    imageUrl,
    iconChar: getIconForGlyph(glyph),
    iconStyle: getSubcategoryIconStyle(fontInfo.fontName)
  };
}
interface ChecklistSidebarViewProps {
  onBack: () => void;
  isMobilePane?: boolean;
  onSearchFocus?: () => void;
}
const HERO_IMAGE_HEIGHT_PX = 232;
const HERO_IMAGE_SIZE_OVERRIDE: React.CSSProperties = {
  backgroundSize: `auto ${HERO_IMAGE_HEIGHT_PX}px`,
  backgroundPosition: 'top center',
  backgroundRepeat: 'no-repeat'
};
const CapsuleImage: React.FC<{
  url: string;
}> = t0 => {
  const $ = _c(4);
  const {
    url
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cx(overlayBorderStyles(), css({
      width: "[40px]",
      flexShrink: "0",
      borderRadius: "6"
    }));
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = css({
      borderRadius: "6"
    });
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== url) {
    t3 = <div className={t1}><img loading="lazy" alt="Game cover art" src={url} className={t2} /></div>;
    $[2] = url;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
interface ChecklistLayoutProps {
  heroImageUrl: string;
  capsuleImageUrl: string;
  isMobilePane: boolean;
  onBack: () => void;
  backLabel: string;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  byLine: React.ReactNode;
  foundCount: number;
  totalCount: number;
  onProgressClick?: () => void;
  renderSearchFilters: (inputRef: React.Ref<HTMLInputElement>) => React.ReactNode;
  searchAriaLabel: string;
  isLoginModalOpen: boolean;
  onLoginModalOpenChange: (open: boolean) => void;
  hero?: React.ReactNode;
  onSearchFocus?: () => void;
  children: React.ReactNode;
}
const ChecklistLayout: React.FC<ChecklistLayoutProps> = t0 => {
  const $ = _c(28);
  const {
    heroImageUrl,
    capsuleImageUrl,
    isMobilePane,
    onBack,
    backLabel,
    title,
    subtitle,
    byLine,
    foundCount,
    totalCount,
    onProgressClick,
    renderSearchFilters,
    searchAriaLabel,
    isLoginModalOpen,
    onLoginModalOpenChange,
    hero,
    onSearchFocus,
    children
  } = t0;
  const searchInputRef = useRef(null);
  let t1;
  if ($[0] !== byLine || $[1] !== capsuleImageUrl || $[2] !== hero || $[3] !== isMobilePane || $[4] !== subtitle || $[5] !== title) {
    t1 = hero ?? <FlexRow gap="12" align="center" width="full" data-drawer-drag="true"><CapsuleImage url={capsuleImageUrl} /><FlexColumn className={css.raw({
        minWidth: "0",
        overflowWrap: "break-word"
      })}>{title}{!isMobilePane && subtitle}{byLine}</FlexColumn></FlexRow>;
    $[0] = byLine;
    $[1] = capsuleImageUrl;
    $[2] = hero;
    $[3] = isMobilePane;
    $[4] = subtitle;
    $[5] = title;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== foundCount || $[8] !== onProgressClick || $[9] !== totalCount) {
    t2 = <MapDiscoveryProgress foundCount={foundCount} totalCount={totalCount} onClick={onProgressClick} />;
    $[7] = foundCount;
    $[8] = onProgressClick;
    $[9] = totalCount;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== renderSearchFilters) {
    t3 = renderSearchFilters(searchInputRef);
    $[11] = renderSearchFilters;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t2 || $[14] !== t3) {
    t4 = <FlexColumn gap="8" width="full">{t2}{t3}</FlexColumn>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== backLabel || $[17] !== children || $[18] !== heroImageUrl || $[19] !== isLoginModalOpen || $[20] !== isMobilePane || $[21] !== onBack || $[22] !== onLoginModalOpenChange || $[23] !== onSearchFocus || $[24] !== searchAriaLabel || $[25] !== t1 || $[26] !== t4) {
    t5 = <SidebarHeroFrame backgroundImageUrl={heroImageUrl} backgroundImageStyleOverride={HERO_IMAGE_SIZE_OVERRIDE} isMobilePane={isMobilePane} onBack={onBack} backLabel={backLabel} searchAriaLabel={searchAriaLabel} searchInputRef={searchInputRef} isLoginModalOpen={isLoginModalOpen} onLoginModalOpenChange={onLoginModalOpenChange} loginModalTrigger="checklist-item" onSearchFocus={onSearchFocus} gap="16">{t1}{t4}{children}</SidebarHeroFrame>;
    $[16] = backLabel;
    $[17] = children;
    $[18] = heroImageUrl;
    $[19] = isLoginModalOpen;
    $[20] = isMobilePane;
    $[21] = onBack;
    $[22] = onLoginModalOpenChange;
    $[23] = onSearchFocus;
    $[24] = searchAriaLabel;
    $[25] = t1;
    $[26] = t4;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  return t5;
};
export const ChecklistSidebarView: React.FC<ChecklistSidebarViewProps> = ({
  onBack,
  isMobilePane = false,
  onSearchFocus
}) => {
  const {
    t
  } = useT(['game_map']);
  const titleName = useAppSelector(selectTitleName) || t($ => $.unknown_game);
  const steamId = useAppSelector(selectSteamId);
  const externalMapId = useAppSelector(selectExternalMapId);
  const mapData = useAppSelector(selectMapData);
  const mapName = mapData?.metadata.name;
  const token = useAppSelector(selectAuthToken);
  const dispatch = useAppDispatch();
  const activeChecklistSubcategory = useAppSelector(selectActiveChecklistSubcategory);
  const filters = useAppSelector(selectFilterState);
  const analytics = useAnalytics();
  const hostType = useHostType();
  const {
    selectMarkerInMap
  } = useMapPinOperations();
  const {
    showChecklistCategories,
    showSingleSubcategory
  } = useChecklistNavigation();
  const focusedChecklistFiltersRef = useRef<FilterState | null>(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const {
    screen,
    searchQuery,
    handleSearchChange,
    completionFilter,
    setCompletionFilter,
    filteredCategoryGroups,
    subcategoryImageMap,
    totalChecklistItems,
    foundChecklistItems,
    subcategoryItems,
    foundSubcategoryItems,
    groupedFilteredItems,
    checkedItems,
    selectedMarkerId,
    isLoggedIn,
    handleToggleItem,
    navigateToSubcategory,
    navigateToCategories
  } = useChecklistState();
  const restoreFocusedChecklistFilters = useCallback(() => {
    if (!focusedChecklistFiltersRef.current) return;
    dispatch(setFilterStateFromUrl(focusedChecklistFiltersRef.current));
    focusedChecklistFiltersRef.current = null;
  }, [dispatch]);
  useEffect(() => {
    if (!activeChecklistSubcategory) return;
    if (!focusedChecklistFiltersRef.current) {
      focusedChecklistFiltersRef.current = structuredClone(filters);
    }
    if (screen.type === 'subcategory' && screen.subcategoryId === activeChecklistSubcategory.subcategoryId) {
      return;
    }
    showSingleSubcategory(activeChecklistSubcategory.subcategoryId);
    navigateToSubcategory(activeChecklistSubcategory.categoryId, activeChecklistSubcategory.subcategoryId, activeChecklistSubcategory.subcategoryTitle);
  }, [activeChecklistSubcategory, filters, navigateToSubcategory, screen, showSingleSubcategory]);
  useEffect(() => restoreFocusedChecklistFilters, [restoreFocusedChecklistFilters]);
  const {
    data: savedChecklists,
    isError: savedChecklistsError
  } = useListSavedChecklistsQuery({
    mapExternalId: externalMapId ?? '',
    token: token ?? ''
  }, {
    skip: !isLoggedIn || !externalMapId || !token
  });
  const pinFeatureMap = useMemo(() => {
    const map = new Map<string, MapDataLocationFeature>();
    if (!mapData) return map;
    for (const feature of mapData.geojson.features) {
      if (feature.geometry.type === 'Point') {
        map.set(feature.properties.externalId, feature);
      }
    }
    return map;
  }, [mapData]);
  const subcategoryMap = useMemo(() => mapData ? buildSubcategoryMap(mapData) : null, [mapData]);
  const filteredSavedChecklists = useMemo(() => {
    if (!savedChecklists) return [];
    const query = searchQuery.trim().toLowerCase();
    return savedChecklists.filter(checklist => {
      if (query) {
        const matches = checklist.title.toLowerCase().includes(query) || checklist.description.toLowerCase().includes(query);
        if (!matches) return false;
      }
      if (completionFilter === 'all') return true;
      const foundCount = checklist.pinIds.reduce((n, id) => n + (checkedItems.has(id) ? 1 : 0), 0);
      const isComplete = foundCount === checklist.pinIds.length;
      return completionFilter === 'completed' ? isComplete : !isComplete;
    });
  }, [savedChecklists, searchQuery, completionFilter, checkedItems]);
  const showOnboardingEmptyState = savedChecklistsError || savedChecklists !== undefined && savedChecklists.length === 0;
  const showSavedChecklistsSection = showOnboardingEmptyState || filteredSavedChecklists.length > 0;
  const handleOpenCustomChecklist = useCallback((checklistId: string, pinCount: number) => {
    dispatch(setActiveCustomChecklistId(checklistId));
    dispatch(setSidebarView('customChecklist'));
    if (analytics && hostType && externalMapId) {
      analytics.event('game_map_saved_checklist_open', {
        mapId: externalMapId,
        mapName,
        titleName,
        checklistId,
        pinCount,
        platform: hostType
      });
    }
  }, [dispatch, analytics, hostType, externalMapId, mapName, titleName]);
  const [bulkUpdatePins] = useBulkUpdatePinStatusesMutation();
  const [deleteChecklistMutation, {
    isLoading: isDeletingChecklist
  }] = useDeleteSavedChecklistMutation();
  const [pendingDeleteChecklistId, setPendingDeleteChecklistId] = useState<string | null>(null);
  const markPinsFound = useCallback(async (pinIds: string[], found: boolean) => {
    if (!externalMapId || pinIds.length === 0) return;
    if (!isLoggedIn) {
      setIsLoginModalOpen(true);
      return;
    }
    try {
      await bulkUpdatePins({
        externalId: externalMapId,
        pins: pinIds.map(id_0 => ({
          pinnableType: 'pin',
          pinnableId: id_0
        })),
        found
      }).unwrap();
      if (analytics && hostType) {
        analytics.event('game_map_bulk_update_pin_status', {
          mapId: externalMapId,
          found,
          pinCount: pinIds.length,
          trigger: 'sidebar-context-menu',
          platform: hostType
        });
      }
    } catch {
      toast({
        description: found ? t($_0 => $_0.mark_all_found_error) : t($_1 => $_1.mark_all_unfound_error),
        variant: 'error'
      });
    }
  }, [bulkUpdatePins, externalMapId, isLoggedIn, t, analytics, hostType]);
  const getSubcategoryPinIds = useCallback((subcategoryId: string) => {
    if (!mapData) return [];
    return mapData.geojson.features.filter(f => f.geometry.type === 'Point' && f.properties.subcategoryExternalId === subcategoryId).map(f_0 => f_0.properties.externalId);
  }, [mapData]);
  const handleConfirmDeleteCustomList = useCallback(async () => {
    if (!externalMapId || !token || !pendingDeleteChecklistId) return;
    try {
      await deleteChecklistMutation({
        mapExternalId: externalMapId,
        id: pendingDeleteChecklistId,
        token
      }).unwrap();
      if (analytics && hostType) {
        analytics.event('game_map_saved_checklist_delete', {
          mapId: externalMapId,
          mapName,
          titleName,
          checklistId: pendingDeleteChecklistId,
          trigger: 'context-menu',
          platform: hostType
        });
      }
      toast({
        description: t($_3 => $_3.save_checklist.checklist_deleted)
      });
      setPendingDeleteChecklistId(null);
    } catch {
      toast({
        description: t($_2 => $_2.save_checklist.couldnt_delete_checklist),
        variant: 'error'
      });
    }
  }, [deleteChecklistMutation, externalMapId, token, t, pendingDeleteChecklistId, analytics, hostType, mapName, titleName]);
  const handleToggleItemWithLogin = useCallback((itemId: string) => {
    if (!isLoggedIn) {
      setIsLoginModalOpen(true);
      return;
    }
    handleToggleItem(itemId);
  }, [isLoggedIn, handleToggleItem]);
  const handleDiscoveryProgressClick = useCallback(() => {
    if (!isLoggedIn) {
      setIsLoginModalOpen(true);
    }
  }, [isLoggedIn]);
  const handleSubcategoryClick = useCallback((categoryId: string, subcategoryId_0: string, title: string) => {
    dispatch(setActiveChecklistSubcategory(null));
    showSingleSubcategory(subcategoryId_0);
    navigateToSubcategory(categoryId, subcategoryId_0, title);
  }, [dispatch, navigateToSubcategory, showSingleSubcategory]);
  const handleBackToCategories = useCallback(() => {
    dispatch(setActiveChecklistSubcategory(null));
    showChecklistCategories();
    navigateToCategories();
  }, [dispatch, navigateToCategories, showChecklistCategories]);
  const handleBack = useCallback(() => {
    restoreFocusedChecklistFilters();
    onBack();
  }, [onBack, restoreFocusedChecklistFilters]);
  const activeSubcategoryId = screen.type === 'subcategory' ? screen.subcategoryId : undefined;
  const handleSubcategoryFeedback = useChecklistFeedback('checklist-subcategory', {
    subcategoryId: activeSubcategoryId
  });
  const heroImageUrl = steamId ? getSteamImageUrl(steamId, 'hero') : '';
  const capsuleImageUrl = steamId ? getWandSteamCdnImageUrl(steamId, 'capsule') : '';
  const progressClickHandler = !isLoggedIn ? handleDiscoveryProgressClick : undefined;
  const byLine = <Type variant="caption" color="text.secondary" className={css({
    marginTop: '8'
  })}>
            {t($_4 => $_4.checklist_pages.by_team_wand)}
        </Type>;
  if (screen.type === 'subcategory') {
    const checklistHero = <FlexColumn align="center" gap="12" width="full" data-drawer-drag="true">
                <ExploreActionHeaderIcon icon="checklist" tone="checklist" />
                <FlexColumn align="center" gap="4" width="full">
                    <Type variant="header-m" color="white" truncate title={screen.subcategoryTitle} className={css({
          textAlign: 'center'
        })}>
                        {screen.subcategoryTitle}
                    </Type>
                    <Type variant="body-s" color="text.secondary" title={t($_5 => $_5.checklist_pages.title_checklist, {
          titleName
        })} className={css({
          textAlign: 'center'
        })} style={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2,
          overflow: 'hidden'
        }}>
                        {t($_6 => $_6.checklist_pages.title_checklist, {
            titleName
          })}
                    </Type>
                    {byLine}
                </FlexColumn>
            </FlexColumn>;
    return <ChecklistLayout heroImageUrl={heroImageUrl} capsuleImageUrl={capsuleImageUrl} isMobilePane={isMobilePane} onBack={handleBackToCategories} backLabel={t($_7 => $_7.checklist_pages.back)} title={<Type variant="header-m" color="white" truncate title={screen.subcategoryTitle}>
                        {screen.subcategoryTitle}
                    </Type>} subtitle={<Type variant="body-s" color="text.secondary" title={t($_8 => $_8.checklist_pages.title_checklist, {
      titleName
    })} style={{
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
      overflow: 'hidden'
    }}>
                        {t($_9 => $_9.checklist_pages.title_checklist, {
        titleName
      })}
                    </Type>} byLine={byLine} foundCount={foundSubcategoryItems} totalCount={subcategoryItems.length} onProgressClick={progressClickHandler} renderSearchFilters={inputRef => <SearchFilters searchQuery={searchQuery} onSearchChange={handleSearchChange} completionFilter={completionFilter} onCompletionFilterChange={setCompletionFilter} inputRef={inputRef} onFocus={onSearchFocus} />} searchAriaLabel={t($_10 => $_10.checklist_pages.search_checklist_items_aria_label)} isLoginModalOpen={isLoginModalOpen} onLoginModalOpenChange={open => {
      if (!open) setIsLoginModalOpen(false);
    }} hero={checklistHero} onSearchFocus={onSearchFocus}>
                <ChecklistItems groupedItems={groupedFilteredItems} checkedItems={checkedItems} onToggleItem={handleToggleItemWithLogin} onShowOnMap={itemId_0 => selectMarkerInMap(itemId_0, {
        flyTo: true,
        selectSource: 'sidebar_checklist'
      })} totalItems={subcategoryItems.length} selectedItemId={selectedMarkerId ?? activeChecklistSubcategory?.pinId} />
                {subcategoryItems.length > 0 && <ChecklistFeedback onRate={handleSubcategoryFeedback} />}
            </ChecklistLayout>;
  }
  return <>
            <ChecklistLayout heroImageUrl={heroImageUrl} capsuleImageUrl={capsuleImageUrl} isMobilePane={isMobilePane} onBack={handleBack} backLabel={t($_11 => $_11.session_history.back)} title={<Type variant="header-m" color="white">
                        {t($_12 => $_12.checklist_pages.title_checklist, {
        titleName
      })}
                    </Type>} subtitle={<Type variant="body-s" color="text.secondary">
                        {t($_13 => $_13.checklist_pages.complete_map_guide_description, {
        titleName
      })}
                    </Type>} byLine={byLine} foundCount={foundChecklistItems} totalCount={totalChecklistItems} onProgressClick={progressClickHandler} renderSearchFilters={inputRef_0 => <SearchFilters searchQuery={searchQuery} onSearchChange={handleSearchChange} completionFilter={completionFilter} onCompletionFilterChange={setCompletionFilter} inputRef={inputRef_0} onFocus={onSearchFocus} />} searchAriaLabel={t($_14 => $_14.checklist_pages.search_checklist_items_aria_label)} isLoginModalOpen={isLoginModalOpen} onLoginModalOpenChange={open_0 => {
      if (!open_0) setIsLoginModalOpen(false);
    }} onSearchFocus={onSearchFocus}>
                <FlexColumn gap="10" width="full">
                    {showSavedChecklistsSection && <Collapsible variant="ghost" defaultOpen={true} className={{
          width: 'full'
        }} iconProps={{
          size: 'md'
        }} trigger={<Type variant="caption" color="brand.primary">
                                    {t($_15 => $_15.save_checklist.your_checklists)}
                                </Type>} triggerClassName={{
          borderRadius: '8',
          justifyContent: 'space-between',
          gap: '8'
        }} triggerStyle={{
          padding: '4px 4px 4px 8px'
        }} contentClassName={{
          padding: '0',
          paddingTop: '8'
        }}>
                            <FlexColumn width="full" gap="8">
                                {showOnboardingEmptyState && <FlexRow gap="4" align="center" justify="center" className={css.raw({
              padding: '[16px 24px]',
              backgroundColor: 'whiteAlpha.5',
              borderRadius: '12'
            })}>
                                        <Type variant="body-xs" color="text.secondary" className={css({
                textAlign: 'center'
              })}>
                                            <Trans ns="game_map" i18nKey={$_16 => $_16.save_checklist.empty_state_track_own_progress} components={{
                  explore: <span className={css({
                    color: 'white',
                    whiteSpace: 'nowrap'
                  })}>
                                                            <Icon icon="travel_explore" size="sm" className={css({
                      color: 'white',
                      display: 'inline-flex',
                      verticalAlign: 'middle',
                      marginRight: '4'
                    })} decorative />
                                                        </span>
                }} />
                                        </Type>
                                    </FlexRow>}
                                {filteredSavedChecklists.map(checklist_0 => {
              const {
                foundCount: foundCount_0,
                imageUrl,
                iconChar,
                iconStyle
              } = getSavedChecklistCardDisplay(checklist_0, checkedItems, pinFeatureMap, subcategoryMap);
              return <ChecklistCard key={checklist_0.id} imageUrl={imageUrl} icon={<div className={css({
                width: '[32px]',
                height: '[32px]',
                borderRadius: 'full',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: '[0]',
                fontSize: '2xl',
                color: 'brand.primary',
                marginLeft: '[5px]'
              })} style={iconStyle}>
                                                    {iconChar}
                                                </div>} title={checklist_0.title} foundCount={foundCount_0} totalCount={checklist_0.pinIds.length} titleColor="brand.primary" onClick={() => handleOpenCustomChecklist(checklist_0.id, checklist_0.pinIds.length)} contextMenuItems={<>
                                                    <ContextMenuItem value="mark-found" leadingIcon="check_circle" onSelect={() => markPinsFound(checklist_0.pinIds, true)}>
                                                        {t($_17 => $_17.mark_all_found)}
                                                    </ContextMenuItem>
                                                    <ContextMenuItem value="mark-unfound" leadingIcon="cancel" onSelect={() => markPinsFound(checklist_0.pinIds, false)}>
                                                        {t($_18 => $_18.mark_all_unfound)}
                                                    </ContextMenuItem>
                                                    <ContextMenuItem value="delete-list" leadingIcon="delete" destructive onSelect={() => setPendingDeleteChecklistId(checklist_0.id)}>
                                                        {t($_19 => $_19.save_checklist.delete_list)}
                                                    </ContextMenuItem>
                                                </>} />;
            })}
                            </FlexColumn>
                        </Collapsible>}
                    {filteredCategoryGroups.length === 0 ? <FlexColumn width="full" align="center" className={css.raw({
          padding: '24'
        })}>
                            <Type variant="body-s" color="text.secondary">
                                {t($_20 => $_20.no_results_found)}
                            </Type>
                        </FlexColumn> : filteredCategoryGroups.map(group => <Collapsible key={group.categoryId} variant="ghost" defaultOpen={true} className={{
          width: 'full'
        }} iconProps={{
          size: 'md'
        }} trigger={<Type variant="caption" color="white">
                                        {group.title}
                                    </Type>} triggerClassName={{
          borderRadius: '8',
          justifyContent: 'space-between',
          gap: '8'
        }} triggerStyle={{
          padding: '4px 4px 4px 8px'
        }} contentClassName={{
          padding: '0',
          paddingTop: '8'
        }}>
                                <FlexColumn width="full" gap="8">
                                    {group.subcategories.map(subcategory => <ChecklistSubcategoryCard key={subcategory.subcategoryId} subcategory={subcategory} imageUrl={subcategoryImageMap.get(subcategory.subcategoryId) ?? null} onClick={() => handleSubcategoryClick(subcategory.categoryId, subcategory.subcategoryId, subcategory.title)} contextMenuItems={<>
                                                    <ContextMenuItem value="mark-found" leadingIcon="check_circle" onSelect={() => markPinsFound(getSubcategoryPinIds(subcategory.subcategoryId), true)}>
                                                        {t($_21 => $_21.mark_all_found)}
                                                    </ContextMenuItem>
                                                    <ContextMenuItem value="mark-unfound" leadingIcon="cancel" onSelect={() => markPinsFound(getSubcategoryPinIds(subcategory.subcategoryId), false)}>
                                                        {t($_22 => $_22.mark_all_unfound)}
                                                    </ContextMenuItem>
                                                </>} />)}
                                </FlexColumn>
                            </Collapsible>)}
                </FlexColumn>
            </ChecklistLayout>
            <DeleteChecklistConfirmModal isOpen={pendingDeleteChecklistId !== null} onClose={() => setPendingDeleteChecklistId(null)} onConfirm={handleConfirmDeleteCustomList} isDeleting={isDeletingChecklist} />
        </>;
};